/**
 * Same connect contract as talk.html / mobile clients:
 * POST /api/livekit/connect → { url, token, room, agent_dispatched, ... }
 */

export type LiveKitConnectRequest = {
  room_name: string
  agent_name?: string
  agent_id?: string
  user_identity: string
  user_name: string
}

export type LiveKitConnectResponse = {
  url: string
  token: string
  room?: string
  agent_name?: string
  agent_id?: string
  agent_dispatched?: boolean
}

export type PanelRoom = {
  name: string
  num_participants: number
  creation_time?: number | null
  active?: boolean
  is_default?: boolean
}

export type PanelAgent = {
  id: string
  name: string
  livekit_agent_name?: string
  /** Placement: agent currently in a room / job. */
  running: boolean
  /**
   * LiveKit reports this agent (room AGENT participant or agent dispatch).
   * Talk selection lists only ``online === true``.
   */
  online?: boolean
  online_reason?: string
  /** Idle connected worker waiting for a room job. */
  status?: string
  /** LiveKit worker id (AW_…) when heartbeated to ACME AI. */
  worker_id?: string
  /** Presence source when discovered from LiveKit (not upload). */
  source?: string
  role?: string
  repo?: string
  /** Room this named agent currently occupies (one room at a time). */
  room?: string | null
  identities?: string[]
  prompt_path?: string
  packages?: string[]
}

export type PanelSession = {
  user_identity: string
  room: string
  url?: string
  age_seconds?: number
}

export type PanelSnapshot = {
  backend: {
    status: string
    livekit: string
    livekit_url: string
    /** Local Docker LiveKit API key (from LIVEKIT_API_KEY / livekit.yaml). */
    livekit_api_key?: string
    /** Local Docker LiveKit API secret (from LIVEKIT_API_SECRET / livekit.yaml). */
    livekit_api_secret?: string
  }
  default_room: string
  agents: PanelAgent[]
  rooms: PanelRoom[]
  sessions: PanelSession[]
  recent_dispatches: Array<{
    room: string
    agent_name: string
    age_seconds: number
    in_cooldown: boolean
  }>
}

export type LiveKitServerInfo = {
  url: string
  api_key: string
  api_secret: string
  configured: boolean
}

export type RoomTrack = {
  sid: string
  type: string
  name: string
  muted: boolean
  source: string
}

export type RoomParticipant = {
  identity: string
  name: string
  sid: string
  kind: string
  state: string
  is_publisher: boolean
  tracks: RoomTrack[]
}

export function buildConnectBody(
  userIdentity: string,
  userName: string,
  roomName: string,
  agentName = '',
  agentId?: string,
): LiveKitConnectRequest {
  return {
    room_name: roomName,
    agent_name: agentName || undefined,
    agent_id: agentId || undefined,
    user_identity: userIdentity,
    user_name: userName,
  }
}

/** Hint when mic APIs are blocked (non-HTTPS LAN IP, etc.). */
export function micAvailabilityHint(
  isSecureContext: boolean,
  hasMediaDevices: boolean,
): string | null {
  if (hasMediaDevices && isSecureContext) return null
  if (!isSecureContext) {
    return (
      'Microphone needs a secure context (https:// or http://localhost). ' +
      'Open Talk via localhost, not a LAN IP over plain http.'
    )
  }
  return 'navigator.mediaDevices is unavailable in this browser context.'
}

/** Agents available for Talk selection (LiveKit presence / online). */
export function selectableAgents(agents: PanelAgent[] | undefined | null): PanelAgent[] {
  return (agents ?? []).filter((a) => a.online === true)
}

/**
 * Resolve API paths for the ACME AI backend.
 * Empty / unset backend URL → same-origin relative `/api/...` (Vite proxies
 * to :8801 in `npm run dev`). Absolute URL still works for direct backends.
 */
export function baseUrl(backendUrl: string): string {
  return (backendUrl || '').replace(/\/+$/, '')
}

function apiPath(backendUrl: string, path: string): string {
  const base = baseUrl(backendUrl)
  const suffix = path.startsWith('/') ? path : `/${path}`
  return base ? `${base}${suffix}` : suffix
}

export function connectEndpoint(backendUrl: string): string {
  return apiPath(backendUrl, '/api/livekit/connect')
}

export function healthEndpoint(backendUrl: string): string {
  return apiPath(backendUrl, '/api/health')
}

export function panelEndpoint(backendUrl: string): string {
  return apiPath(backendUrl, '/api/livekit/panel')
}

export function livekitServerEndpoint(backendUrl: string): string {
  return apiPath(backendUrl, '/api/livekit/server')
}

export function panelStreamEndpoint(backendUrl: string): string {
  return apiPath(backendUrl, '/api/livekit/panel/stream')
}

export function panelWsEndpoint(backendUrl: string): string {
  const httpPath = apiPath(backendUrl, '/api/livekit/panel/ws')
  if (httpPath.startsWith('https://')) {
    return `wss://${httpPath.slice('https://'.length)}`
  }
  if (httpPath.startsWith('http://')) {
    return `ws://${httpPath.slice('http://'.length)}`
  }
  // Same-origin relative path → use page location.
  if (typeof window !== 'undefined' && window.location) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${proto}//${window.location.host}${httpPath}`
  }
  return httpPath.replace(/^http/, 'ws')
}

export type PanelSubscription = {
  close: () => void
}

export type SubscribePanelOptions = {
  /** Poll interval when SSE/WS unavailable (default 2000ms). */
  pollIntervalMs?: number
  /** Pass ``null`` to skip SSE and fall through to poll. */
  EventSourceImpl?: typeof EventSource | null
  /** Pass ``null`` to skip WebSocket and fall through to SSE/poll. */
  WebSocketImpl?: typeof WebSocket | null
  fetchImpl?: typeof fetch
  /** Override setInterval (tests). */
  setIntervalFn?: typeof setInterval
  clearIntervalFn?: typeof clearInterval
}

/**
 * Live panel updates: WebSocket first (agent state sync), then SSE, then poll.
 */
export function subscribePanelUpdates(
  backendUrl: string,
  onSnapshot: (snap: PanelSnapshot) => void,
  onError?: (err: Error) => void,
  options: SubscribePanelOptions = {},
): PanelSubscription {
  const pollIntervalMs = options.pollIntervalMs ?? 2_000
  const fetchImpl = options.fetchImpl ?? fetch
  const setIntervalFn = options.setIntervalFn ?? setInterval
  const clearIntervalFn = options.clearIntervalFn ?? clearInterval
  // ``null`` explicitly disables a transport; ``undefined`` uses the global.
  const ES =
    options.EventSourceImpl === null
      ? null
      : (options.EventSourceImpl ?? globalThis.EventSource)
  const WS =
    options.WebSocketImpl === null
      ? null
      : (options.WebSocketImpl ?? globalThis.WebSocket)

  let closed = false
  let pollId: ReturnType<typeof setInterval> | null = null
  let es: EventSource | null = null
  let ws: WebSocket | null = null
  let usingPoll = false
  let pingId: ReturnType<typeof setInterval> | null = null

  const startPoll = () => {
    if (closed || usingPoll) return
    usingPoll = true
    const tick = async () => {
      if (closed) return
      try {
        const snap = await fetchPanelSnapshot(backendUrl, fetchImpl)
        if (!closed) onSnapshot(snap)
      } catch (err) {
        if (!closed) {
          onError?.(err instanceof Error ? err : new Error(String(err)))
        }
      }
    }
    void tick()
    pollId = setIntervalFn(() => {
      void tick()
    }, pollIntervalMs)
  }

  const startSse = () => {
    if (closed) return
    if (typeof ES !== 'function') {
      startPoll()
      return
    }
    try {
      es = new ES(panelStreamEndpoint(backendUrl))
      es.onmessage = (ev: MessageEvent<string>) => {
        if (closed) return
        try {
          onSnapshot(JSON.parse(ev.data) as PanelSnapshot)
        } catch (err) {
          onError?.(err instanceof Error ? err : new Error(String(err)))
        }
      }
      es.onerror = () => {
        if (closed) return
        es?.close()
        es = null
        startPoll()
      }
    } catch {
      startPoll()
    }
  }

  const startWs = () => {
    if (closed) return
    if (typeof WS !== 'function') {
      startSse()
      return
    }
    try {
      ws = new WS(panelWsEndpoint(backendUrl))
      ws.onopen = () => {
        // Keepalive so the server loop wakes on receive as well as timeout.
        pingId = setIntervalFn(() => {
          if (ws && ws.readyState === WS.OPEN) {
            try {
              ws.send('ping')
            } catch {
              /* ignore */
            }
          }
        }, Math.max(pollIntervalMs, 5_000))
      }
      ws.onmessage = (ev: MessageEvent<string>) => {
        if (closed) return
        try {
          const data =
            typeof ev.data === 'string' ? ev.data : String(ev.data ?? '')
          onSnapshot(JSON.parse(data) as PanelSnapshot)
        } catch (err) {
          onError?.(err instanceof Error ? err : new Error(String(err)))
        }
      }
      ws.onerror = () => {
        /* onclose handles fallback */
      }
      ws.onclose = () => {
        if (pingId != null) {
          clearIntervalFn(pingId)
          pingId = null
        }
        ws = null
        if (!closed) startSse()
      }
    } catch {
      startSse()
    }
  }

  startWs()

  return {
    close: () => {
      closed = true
      if (pingId != null) {
        clearIntervalFn(pingId)
        pingId = null
      }
      try {
        ws?.close()
      } catch {
        /* ignore */
      }
      ws = null
      es?.close()
      es = null
      if (pollId != null) {
        clearIntervalFn(pollId)
        pollId = null
      }
    },
  }
}

export function agentToggleEndpoint(backendUrl: string): string {
  return apiPath(backendUrl, '/api/livekit/agent')
}

export function agentsEndpoint(backendUrl: string): string {
  return apiPath(backendUrl, '/api/livekit/agents')
}

export function roomsListEndpoint(backendUrl: string): string {
  return apiPath(backendUrl, '/api/livekit/rooms')
}

export function roomParticipantsEndpoint(
  backendUrl: string,
  roomName: string,
): string {
  return apiPath(
    backendUrl,
    `/api/livekit/rooms/${encodeURIComponent(roomName)}/participants`,
  )
}

async function readJsonOrThrow(
  resp: Response,
  label: string,
): Promise<unknown> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`${label} returned ${resp.status}${text ? `: ${text}` : ''}`)
  }
  return resp.json()
}

export async function fetchLiveKitConnect(
  backendUrl: string,
  body: LiveKitConnectRequest,
  fetchImpl: typeof fetch = fetch,
): Promise<LiveKitConnectResponse> {
  const resp = await fetchImpl(connectEndpoint(backendUrl), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = (await readJsonOrThrow(resp, 'Backend')) as Partial<LiveKitConnectResponse>
  if (!data.url || !data.token) {
    throw new Error('Backend response missing LiveKit url or token')
  }
  return data as LiveKitConnectResponse
}

export async function fetchTrainerConnect(
  backendUrl: string,
  trainerToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LiveKitConnectResponse> {
  const resp = await fetchImpl(apiPath(backendUrl, '/api/trainer/connect'), {
    method: 'POST',
    headers: { 'X-Trainer-Token': trainerToken },
  })
  const data = (await readJsonOrThrow(resp, 'Trainer')) as Partial<LiveKitConnectResponse>
  if (!data.url || !data.token) {
    throw new Error('Trainer response missing LiveKit url or token')
  }
  return data as LiveKitConnectResponse
}

export async function fetchPanelSnapshot(
  backendUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PanelSnapshot> {
  const resp = await fetchImpl(panelEndpoint(backendUrl), { method: 'GET' })
  return (await readJsonOrThrow(resp, 'Panel API')) as PanelSnapshot
}

export async function fetchLiveKitServer(
  backendUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LiveKitServerInfo> {
  const resp = await fetchImpl(livekitServerEndpoint(backendUrl), {
    method: 'GET',
  })
  return (await readJsonOrThrow(resp, 'LiveKit server API')) as LiveKitServerInfo
}

export async function fetchRoomParticipants(
  backendUrl: string,
  roomName: string,
  fetchImpl: typeof fetch = fetch,
): Promise<RoomParticipant[]> {
  const resp = await fetchImpl(roomParticipantsEndpoint(backendUrl, roomName), {
    method: 'GET',
  })
  const data = (await readJsonOrThrow(resp, 'Participants API')) as {
    participants?: RoomParticipant[]
  }
  return data.participants ?? []
}

export async function createRoom(
  backendUrl: string,
  roomName: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PanelRoom> {
  const resp = await fetchImpl(apiPath(backendUrl, '/api/livekit/rooms'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ room_name: roomName }),
  })
  const data = (await readJsonOrThrow(resp, 'Create room')) as { room: PanelRoom }
  return data.room
}

export async function deleteRoom(
  backendUrl: string,
  roomName: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const resp = await fetchImpl(apiPath(backendUrl, '/api/livekit/rooms/delete'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ room_name: roomName }),
  })
  await readJsonOrThrow(resp, 'Delete room')
}

export type DeleteSessionResult = {
  ok: boolean
  user_identity: string
  cleared: boolean
  room?: string
}

/** Clear one cached backend connect session (Talk Sessions list). */
export async function deleteSession(
  backendUrl: string,
  userIdentity: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DeleteSessionResult> {
  const resp = await fetchImpl(
    apiPath(backendUrl, '/api/livekit/sessions/delete'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_identity: userIdentity }),
    },
  )
  return (await readJsonOrThrow(resp, 'Delete session')) as DeleteSessionResult
}

export async function removeParticipant(
  backendUrl: string,
  roomName: string,
  identity: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const resp = await fetchImpl(
    apiPath(backendUrl, '/api/livekit/participants/remove'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ room_name: roomName, identity }),
    },
  )
  await readJsonOrThrow(resp, 'Remove participant')
}

export async function muteParticipantTrack(
  backendUrl: string,
  body: {
    room_name: string
    identity: string
    track_sid: string
    muted: boolean
  },
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const resp = await fetchImpl(
    apiPath(backendUrl, '/api/livekit/participants/mute'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
  await readJsonOrThrow(resp, 'Mute track')
}

export type AgentToggleAction = 'join' | 'leave'

export type AgentToggleResult = {
  ok: boolean
  action: AgentToggleAction
  room: string
  agent_name: string
  agent_id?: string
  removed?: string[]
  matched?: string[]
  already_present?: boolean
  dispatched?: boolean
  left_rooms?: string[]
}

export async function toggleAgentInRoom(
  backendUrl: string,
  body: {
    room_name: string
    agent_name: string
    agent_id?: string
    action: AgentToggleAction
  },
  fetchImpl: typeof fetch = fetch,
): Promise<AgentToggleResult> {
  const resp = await fetchImpl(agentToggleEndpoint(backendUrl), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return (await readJsonOrThrow(resp, 'Agent toggle')) as AgentToggleResult
}

export async function fetchAgents(
  backendUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PanelAgent[]> {
  const resp = await fetchImpl(agentsEndpoint(backendUrl), { method: 'GET' })
  const data = (await readJsonOrThrow(resp, 'Agents API')) as {
    agents?: PanelAgent[]
  }
  return data.agents ?? []
}

export async function fetchRooms(
  backendUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PanelRoom[]> {
  const resp = await fetchImpl(roomsListEndpoint(backendUrl), { method: 'GET' })
  const data = (await readJsonOrThrow(resp, 'Rooms API')) as {
    rooms?: PanelRoom[]
  }
  return data.rooms ?? []
}

export async function deleteAgent(
  backendUrl: string,
  agentIdOrName: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const resp = await fetchImpl(
    `${agentsEndpoint(backendUrl)}/${encodeURIComponent(agentIdOrName)}`,
    { method: 'DELETE' },
  )
  await readJsonOrThrow(resp, 'Leave agent rooms')
}

/** True when a room participant looks like the configured agent. */
export function participantLooksLikeAgent(
  identity: string,
  agentName: string,
): boolean {
  const low = identity.toLowerCase()
  const needle = agentName.trim().toLowerCase()
  if (needle && (low.includes(needle) || needle.includes(low))) return true
  return low.includes('agent') || low.startsWith('ai-') || low.startsWith('ali')
}

export function isAudioTrack(track: RoomTrack): boolean {
  const t = `${track.type} ${track.source}`.toLowerCase()
  return t.includes('audio') || t.includes('microphone')
}

/** Reject if `promise` does not settle within `ms`. */
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`))
    }, ms)
    promise.then(
      (v) => {
        clearTimeout(t)
        resolve(v)
      },
      (e) => {
        clearTimeout(t)
        reject(e)
      },
    )
  })
}
